import { useUserMe } from "~/components/user/composables/useUser";
import { useApi } from "~/composables/useApi";
import { useSupabase } from "~/composables/useSupabase";
import type { Database } from "@asap/shared/types/supabase.gen";
import { teamMemberColumns } from "@asap/shared/utils/supabase.columns";
import type { TeamMember } from "@asap/shared/utils/supabase.types";
import { useToast } from "~/composables/useToast";

export const useTeamMemberMe = function () {
  const { teamMember } = useUserMe();
  const { getAssignedTeamMemberIds, getSubstituteId } = useTeamMember();
  const { addToastError } = useToast();
  const { t } = useI18n();

  const myAssignedTeamMemberIds = ref<string[]>([]);
  const mySubstituteId = ref<string | null>(null);

  const isAbsent = computed(() => !!mySubstituteId.value);

  const refetchAbsenceState = async () => {
    if (!teamMember.value?.id) return;
    try {
      myAssignedTeamMemberIds.value = await getAssignedTeamMemberIds(teamMember.value.id);
      mySubstituteId.value = await getSubstituteId(teamMember.value.id);
    } catch (error) {
      // Keep the last known values rather than blanking the UI, and surface the failure.
      addToastError({ description: t("teamMembers.absenceStatusFetchError") }, error);
    }
  };

  watch(
    () => teamMember.value?.id,
    (teamMemberId) => {
      if (!teamMemberId) {
        myAssignedTeamMemberIds.value = [];
        mySubstituteId.value = null;
        return;
      }

      void refetchAbsenceState();
    },
    { immediate: true }
  );

  return { teamMember, myAssignedTeamMemberIds, isAbsent, mySubstituteId, refetchAbsenceState };
};

export const useTeamMember = function () {
  const supabase = useSupabase();
  const { invoke } = useApi();

  const getTeamMemberProfile = async function (id: string) {
    const response = await invoke({ path: "team-member/:id", method: "GET", params: { id } });
    if (!response.success) throw response.error;
    return response.data;
  };

  const listOrganigram = async function (filters: { isOffboarded?: boolean }) {
    const response = await invoke({ path: "team-member/organigram", method: "GET", query: filters });
    if (!response.success) throw response.error;
    return response.data.items;
  };

  const selectSingle = async function (id: string) {
    const { data, error } = await supabase
      .from("team_member")
      .select<string, TeamMember>(teamMemberColumns)
      .eq("id", id)
      .single();
    if (error) throw new Error(error.message);
    return { data };
  };

  const update = async function (id: string, teamMember: Database["public"]["Tables"]["team_member"]["Update"]) {
    const { data, error } = await supabase
      .from("team_member")
      .update(teamMember)
      .eq("id", id)
      .select<string, TeamMember>(teamMemberColumns)
      .single();

    if (error) throw new Error(error.message);

    return { data };
  };

  const getAssignedTeamMemberIds = async (assignedToTeamMemberId: string) => {
    const { data, error } = await supabase
      .from("team_member_responsible_during_absence")
      .select("team_member_away_id")
      .eq("team_member_taking_over_id", assignedToTeamMemberId);

    if (error) throw new Error(error.message);

    return data.map((el) => el.team_member_away_id);
  };

  const getSubstituteId = async (teamMemberAwayId: string) => {
    const { data, error } = await supabase
      .from("team_member_responsible_during_absence")
      .select("team_member_taking_over_id")
      .eq("team_member_away_id", teamMemberAwayId)
      .maybeSingle();

    if (error) throw new Error(error.message);

    return data ? data.team_member_taking_over_id : null;
  };

  return {
    getTeamMemberProfile,
    listOrganigram,
    selectSingle,
    update,
    getAssignedTeamMemberIds,
    getSubstituteId,
  };
};

export const useTeamMemberAutonomy = function () {
  const { invoke } = useApi();
  const { addToastSuccess, addToastError } = useToast();
  const { t } = useI18n();

  const setTeamMemberAutonomy = async (id: string, isAutonomous: boolean) => {
    const response = await invoke({
      path: "team-member/:id/autonomy",
      method: "PUT",
      params: { id },
      body: { is_autonomous: isAutonomous },
    });

    if (!response.success) {
      addToastError({
        title: t("teamMembers.autonomy.errorTitle"),
        description: t("teamMembers.autonomy.errorDescription"),
      });
    } else {
      addToastSuccess({
        title: t("teamMembers.autonomy.successTitle"),
        description: t("teamMembers.autonomy.successDescription"),
      });
    }
    return response;
  };

  return { setTeamMemberAutonomy };
};
